/**
 * Cases Dashboard reader — the admin Inbox "Dashboard" tab.
 *
 * One fetch per (view) change plus a slow poll while the tab is
 * visible (the sheet this replaces refreshed hourly; a minute keeps
 * the room's wallboard fresh without queue-speed weight). `saveNotes`
 * writes a case's analyst note and patches the row in place so the
 * table doesn't flash.
 */

import { useCallback, useRef } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { CaseDashboardResponse, DashboardView } from '@/types/caseDashboard';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll, withEtag } from './usePoll';

export const DASHBOARD_POLL_MS = 60_000;

interface UseCaseDashboardParams {
  view: DashboardView;
  /** When false nothing fetches — the Inbox page keeps the hook
   * mounted across tabs but only the Dashboard tab should pay. */
  enabled: boolean;
}

interface UseCaseDashboardResult {
  data: CaseDashboardResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  saveNotes: (reportRequestId: string, notes: string | null) => Promise<void>;
}

export function useCaseDashboard({
  view,
  enabled,
}: UseCaseDashboardParams): UseCaseDashboardResult {
  const viewRef = useRef(view);
  viewRef.current = view;

  const poll = usePoll<CaseDashboardResponse>({
    enabled,
    interval: DASHBOARD_POLL_MS,
    // A different view is a different token space; usePoll drops the
    // stored ETag on every dependency change.
    refetchDeps: [view],
    queryFn: async ({ signal, etag, manual }) => {
      // Scheduled ticks poll on the stored token (steady state: a
      // body-less 304); a user-driven refetch is always a full read.
      const result = await vnextApiClient.getCaseDashboard(viewRef.current, {
        signal,
        etag: manual ? null : etag,
      });
      return result === 'not-modified' ? UNCHANGED : withEtag(result.data, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });
  const { setData } = poll;

  const saveNotes = useCallback(
    async (reportRequestId: string, notes: string | null) => {
      const saved = await vnextApiClient.updateDashboardNotes(reportRequestId, notes);
      setData((prev) =>
        prev
          ? {
              ...prev,
              cases: prev.cases.map((c) =>
                c.reportRequestId === reportRequestId ? { ...c, analystNotes: saved } : c
              ),
            }
          : prev
      );
    },
    [setData]
  );

  return {
    data: poll.data ?? null,
    isLoading: poll.isLoading,
    error: poll.error === undefined ? null : toError(poll.error),
    refetch: poll.refetch,
    saveNotes,
  };
}
